import { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Typography,
    Button,
    MenuItem,
    TextField,
    Stack,
    Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import moment from "moment";
import { ITripItem } from "@interfaces";
import { useAppDispatch } from "src/Redux/store";
import {
    moveItem,
    removeItem,
    resizeItem,
    updateItemQuantity,
    checkTripItemAvailability,
} from "src/Redux/Slices/tripPlanner";
import {
    snapQuantityToNumPax,
    selectedTravellerCount,
    travellerCountForItem,
} from "src/Utilities/tripPlannerHelpers";

interface Props {
    open: boolean;
    item: ITripItem | null;
    tripStartDate: string;
    tripDurationDays: number;
    onClose: () => void;
    onEditTravellers?: (itemId: string) => void;
}

const statusLabel = (s: ITripItem["availabilityStatus"]) =>
    s === "available"
        ? { label: "Available", color: "success" as const }
        : s === "unavailable"
          ? { label: "Unavailable", color: "error" as const }
          : s === "checking"
            ? { label: "Checking…", color: "warning" as const }
            : { label: "Not yet checked", color: "default" as const };

export const TripItemEditModal = ({
    open,
    item,
    tripStartDate,
    tripDurationDays,
    onClose,
    onEditTravellers,
}: Props) => {
    const dispatch = useAppDispatch();
    const [dayIndex, setDayIndex] = useState(0);
    const [quantity, setQuantity] = useState(1);
    const [durationDays, setDurationDays] = useState(1);

    // Re-seed local form whenever a new item is opened.
    useEffect(() => {
        if (!item) return;
        setDayIndex(item.dayIndex);
        setQuantity(item.quantity);
        setDurationDays(item.durationDays);
    }, [item]);

    if (!item) return null;

    const numPax = Math.max(1, item.numPax || 1);
    const maxSpan = Math.max(1, tripDurationDays - dayIndex);
    const startDateLabel = moment(tripStartDate, "YYYY-MM-DD")
        .add(dayIndex, "days")
        .format("ddd, D MMM YYYY");
    const endDateLabel = moment(tripStartDate, "YYYY-MM-DD")
        .add(dayIndex + durationDays - 1, "days")
        .format("ddd, D MMM YYYY");

    const isMultiNight = item.durationDays > 1 || item.productClass === "accommodation";
    const status = statusLabel(item.availabilityStatus);

    const handleSave = () => {
        if (dayIndex !== item.dayIndex) {
            dispatch(moveItem({ itemId: item.id, toDayIndex: dayIndex }));
        }
        if (durationDays !== item.durationDays) {
            dispatch(resizeItem({ itemId: item.id, durationDays }));
        }
        if (quantity !== item.quantity) {
            dispatch(updateItemQuantity({ itemId: item.id, quantity }));
        }
        // Always re-check availability after edits — any of the three changes
        // above invalidates a previous check.
        dispatch(checkTripItemAvailability({ itemId: item.id }));
        onClose();
    };

    const handleRemove = () => {
        dispatch(removeItem({ itemId: item.id }));
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ pr: 5 }}>
                Edit item
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{ position: "absolute", right: 8, top: 8 }}
                    aria-label="Close"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                    {item.productImagePath && (
                        <Box
                            sx={{
                                width: 72,
                                height: 72,
                                borderRadius: 1,
                                background: `#eee center/cover no-repeat url(${item.productImagePath})`,
                                flexShrink: 0,
                            }}
                        />
                    )}
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                            {item.name}
                        </Typography>
                        {item.fareName && (
                            <Typography variant="body2" color="text.secondary" noWrap>
                                {item.fareName}
                            </Typography>
                        )}
                        <Chip
                            size="small"
                            label={status.label}
                            color={status.color}
                            sx={{ mt: 0.5 }}
                        />
                    </Box>
                </Stack>

                <TextField
                    select
                    fullWidth
                    size="small"
                    label="Day"
                    value={dayIndex}
                    onChange={(e) => {
                        const v = Number(e.target.value);
                        setDayIndex(v);
                        // Re-clamp span if the new position has fewer days remaining.
                        const newMax = Math.max(1, tripDurationDays - v);
                        if (durationDays > newMax) setDurationDays(newMax);
                    }}
                    sx={{ mb: 2 }}
                >
                    {Array.from({ length: tripDurationDays }, (_, i) => i).map((i) => (
                        <MenuItem key={i} value={i}>
                            Day {i + 1} —{" "}
                            {moment(tripStartDate, "YYYY-MM-DD")
                                .add(i, "days")
                                .format("ddd, D MMM")}
                        </MenuItem>
                    ))}
                </TextField>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                        Quantity {numPax > 1 ? `(steps of ${numPax})` : ""}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                        <IconButton
                            size="small"
                            onClick={() =>
                                setQuantity((q) => snapQuantityToNumPax(Math.max(numPax, q - numPax), numPax))
                            }
                            disabled={quantity <= numPax}
                            aria-label="Decrease quantity"
                        >
                            <RemoveIcon fontSize="small" />
                        </IconButton>
                        <TextField
                            size="small"
                            type="number"
                            value={quantity}
                            onChange={(e) => {
                                const n = Number(e.target.value) || numPax;
                                setQuantity(snapQuantityToNumPax(n, numPax));
                            }}
                            inputProps={{ min: numPax, step: numPax, style: { width: 60, textAlign: "center" } }}
                        />
                        <IconButton
                            size="small"
                            onClick={() => setQuantity((q) => snapQuantityToNumPax(q + numPax, numPax))}
                            aria-label="Increase quantity"
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="body2" color="text.secondary">
                            × ${item.rrp.toFixed(0)} = ${(item.rrp * quantity).toFixed(0)}
                        </Typography>
                    </Stack>
                </Box>

                {isMultiNight && (
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Nights"
                        value={durationDays}
                        onChange={(e) => setDurationDays(Number(e.target.value))}
                        helperText={`${startDateLabel} → ${endDateLabel}`}
                    >
                        {Array.from({ length: maxSpan }, (_, i) => i + 1).map((d) => (
                            <MenuItem key={d} value={d}>
                                {d} {d === 1 ? "night" : "nights"}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
                {!isMultiNight && (
                    <Typography variant="body2" color="text.secondary">
                        {startDateLabel}
                    </Typography>
                )}

                {onEditTravellers && (
                    <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<PeopleOutlineIcon />}
                        onClick={() => onEditTravellers(item.id)}
                        sx={{ mt: 2 }}
                    >
                        Travellers ({selectedTravellerCount(item)}/{travellerCountForItem(item)})
                    </Button>
                )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
                <Button
                    onClick={handleRemove}
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                >
                    Remove
                </Button>
                <Box>
                    <Button onClick={onClose} sx={{ mr: 1 }}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained">Save</Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};
